import React, { useState } from 'react';
import * as Icons from 'lucide-react';
import AttachmentActions, { isResumeAttachment } from './AttachmentActions';
import EmailBodyFrame from './EmailBodyFrame';

// Decide how an attachment can be previewed in-app.
function previewKind(att) {
  const name = (att?.filename || '').toLowerCase();
  const ct = (att?.content_type || '').toLowerCase();
  if (ct.includes('pdf') || name.endsWith('.pdf')) return 'pdf';
  if (ct.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(name)) return 'image';
  if (/\.(docx?|xlsx?|pptx?|odt|rtf)$/.test(name)
      || ct.includes('word') || ct.includes('officedocument')
      || ct.includes('excel') || ct.includes('powerpoint')) return 'office';
  return 'none';
}

function attachmentIconMeta(att) {
  const name = (att?.filename || '').toLowerCase();
  const ct = (att?.content_type || '').toLowerCase();
  if (ct.includes('pdf') || name.endsWith('.pdf')) {
    return { Icon: Icons.Chrome, className: 'em-att-icon-pdf', label: 'PDF file' };
  }
  if (
    ct.includes('spreadsheet') ||
    ct.includes('excel') ||
    /\.(xlsx?|csv)$/i.test(name)
  ) {
    return { Icon: Icons.FileSpreadsheet, className: 'em-att-icon-excel', label: 'Excel file' };
  }
  if (
    ct.includes('word') ||
    ct.includes('document') ||
    /\.(docx?|rtf|odt)$/i.test(name)
  ) {
    return { Icon: Icons.FileType2, className: 'em-att-icon-word', label: 'Word document' };
  }
  if (ct.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name)) {
    return { Icon: Icons.FileImage, className: 'em-att-icon-image', label: 'Image file' };
  }
  return { Icon: Icons.File, className: 'em-att-icon-default', label: 'Attachment' };
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function getInitials(name) {
  if (!name) return '?';
  const parts = name.split(/[.@\s_]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0]?.slice(0, 2).toUpperCase() || '?';
}

// Inline images in email bodies are referenced by Content-ID (src="cid:...").
// Browsers can't load cid: URLs, so swap them for the real attachment URLs.
function resolveInlineImages(html, attachments) {
  if (!html) return '';
  if (!attachments || !attachments.length) return html;
  let out = html;
  attachments.forEach(att => {
    const url = att.download_url || att.file;
    const cid = (att.content_id || '').replace(/^<|>$/g, '').trim();
    if (!url || !cid) return;
    const esc = cid.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Replace both cid:<id> and any leftover angle-bracket forms.
    out = out.replace(new RegExp('cid:' + esc, 'gi'), url);
  });
  return out;
}

export default function EmailViewer({
  message, onClose, onReply, onReplyAll, onForward,
  onDelete, onArchive, onToggleStar, onToggleFlag, onPrint, onDownload, showToast,
  onMarkUnread, onJunk,
}) {
  const [previewAtt, setPreviewAtt] = useState(null);
  if (!message) {
    return (
      <div className="em-viewer em-viewer-empty">
        <div className="em-viewer-empty-content">
          <Icons.Mail size={48} strokeWidth={1} />
          <h3>Select an item to read</h3>
          <p>Click on an email in the list to read it here</p>
        </div>
      </div>
    );
  }

  const displayNameOf = value => {
    if (!value) return '';
    if (typeof value === 'object') {
      return (
        value.name ||
        value.display_name ||
        value.display ||
        value.full_name ||
        displayNameOf(value.address || value.email)
      );
    }
    const text = String(value).trim();
    const bracketName = text.match(/^(.+?)\s*<[^>]+>$/);
    if (bracketName?.[1]) return bracketName[1].replace(/^"|"$/g, '').trim();
    if (text.includes('@')) {
      return text
        .split('@')[0]
        .replace(/[._-]+/g, ' ')
        .replace(/\b\w/g, ch => ch.toUpperCase());
    }
    return text;
  };

  const namesOf = (display, list) => {
    const names = (list || []).map(displayNameOf).filter(Boolean);
    return names.length ? names.join(', ') : displayNameOf(display);
  };

  const sender = displayNameOf(message.from_display || message.from_name || message.from_address) || 'Unknown';
  const to = namesOf(message.to_display, message.to_addresses);
  const cc = namesOf(message.cc_display, message.cc_addresses);
  // Email addresses for the hover tooltips.
  const emailsOf = list => (list || [])
    .map(a => (typeof a === 'string' ? a : a?.address || a?.email || ''))
    .filter(Boolean)
    .join(', ');
  const fromEmail = message.from_address || '';
  const toEmails = emailsOf(message.to_addresses) || to;
  const ccEmails = emailsOf(message.cc_addresses) || cc;
  // Hide only inline IMAGES (signature logos already shown in the body). Real
  // files like PDFs/Word docs must always show, even when the sender's client
  // tagged them inline / gave them a Content-ID.
  const realAttachments = (message.attachments || []).filter(
    a => !(a.is_inline && (a.content_type || '').toLowerCase().startsWith('image/'))
  );

  // Rendered in two places — near the header (top) and below the body (bottom) —
  // each attachment keeps its Add and download actions.
  const attachmentsBlock = realAttachments.length > 0 ? (
    <div className="em-viewer-attachments">
      <div className="em-viewer-att-list">
        {realAttachments.map(att => {
          const canPreview = previewKind(att) !== 'none';
          const { Icon: AttachmentIcon, className, label } = attachmentIconMeta(att);
          const isExcelAttachment = className === 'em-att-icon-excel';
          return (
            <div key={att.attachment_id || att.id} className="em-viewer-att-item">
              <div
                className={`em-viewer-att-clickable ${canPreview ? 'em-viewer-att-preview' : ''}`}
                onClick={() => canPreview && setPreviewAtt(att)}
                title={canPreview ? 'Click to preview' : att.filename}
              >
                <span className={`em-viewer-att-type-icon ${className}`} title={label}>
                  <AttachmentIcon size={18} />
                </span>
                <div className="em-viewer-att-info">
                  <div className="em-viewer-att-name">{att.filename}</div>
                  <div className="em-viewer-att-size">{att.size ? `${(att.size / 1024).toFixed(1)} KB` : ''}</div>
                </div>
              </div>
              {canPreview && (
                <button className="em-viewer-att-eye" onClick={() => setPreviewAtt(att)} title="Preview">
                  <Icons.Eye size={14} />
                </button>
              )}
              {isResumeAttachment(att) && !isExcelAttachment && (
                <AttachmentActions attachment={att} showToast={showToast} />
              )}
              <a href={att.download_url || att.file || '#'} target="_blank" rel="noopener noreferrer" className="em-viewer-att-dl" title="Download">
                <Icons.Download size={13} />
              </a>
            </div>
          );
        })}
      </div>
    </div>
  ) : null;

  return (
    <div className="em-viewer">
      {/* Outlook (web) style slim command toolbar across the top of the pane. */}
      <div className="em-viewer-toolbar">
        <button className="em-vt-btn" onClick={() => onReply(message)}><Icons.Reply size={16} /><span>Reply</span></button>
        <button className="em-vt-btn" onClick={() => onReplyAll(message)}><Icons.ReplyAll size={16} /><span>Reply All</span></button>
        <button className="em-vt-btn" onClick={() => onForward(message)}><Icons.Forward size={16} /><span>Forward</span></button>
        <span className="em-vt-sep" />
        <button className="em-vt-btn" onClick={() => onDelete(message)}><Icons.Trash2 size={16} /><span>Delete</span></button>
        <button className="em-vt-btn" onClick={() => onMarkUnread && onMarkUnread(message)}><Icons.MailOpen size={16} /><span>Mark Unread</span></button>
        <button className="em-vt-btn" onClick={() => onArchive(message)}><Icons.Archive size={16} /><span>Archive</span></button>
        <button className="em-vt-btn" onClick={() => (onJunk ? onJunk(message) : onDelete(message))}><Icons.Ban size={16} /><span>Junk</span></button>
        <span className="em-vt-spacer" />
        <button className="em-vt-btn" onClick={() => onToggleFlag(message)} title="Flag">
          <Icons.Flag size={16} color={message.is_flagged ? '#ef4444' : undefined} /><span>Flag</span>
        </button>
        <button className="em-vt-btn em-vt-icon" onClick={onClose} title="Close"><Icons.X size={16} /></button>
      </div>

      <div className="em-viewer-header">
        <div className="em-viewer-subject-row">
          <h2 className="em-viewer-subject">{message.subject || '(no subject)'}</h2>
          {message.thread_message_count > 1 && (
            <span className="em-viewer-thread-badge">{message.thread_message_count}</span>
          )}
          <div className="em-preview-head-actions">
            <button className="em-preview-action-btn" onClick={() => onReply(message)} type="button">
              <Icons.Reply size={14} />
              <span>Reply</span>
            </button>
            <button className="em-preview-action-btn" onClick={() => onReplyAll(message)} type="button">
              <Icons.ReplyAll size={14} />
              <span>Reply All</span>
            </button>
            <button className="em-preview-action-btn" onClick={() => onForward(message)} type="button">
              <Icons.Forward size={14} />
              <span>Forward</span>
            </button>
          </div>
        </div>
        <div className="em-viewer-from-row">
          <span className="em-viewer-avatar-lg">{getInitials(sender)}</span>
          <div className="em-viewer-from-details">
            <div className="em-viewer-from-name" title={fromEmail}>{sender}</div>
            <div className="em-viewer-from-addr">to <span title={toEmails}>{to}</span></div>
            {cc && <div className="em-viewer-from-addr">Cc: <span title={ccEmails}>{cc}</span></div>}
          </div>
          <div className="em-viewer-header-right">
            <div className="em-viewer-date">{formatDate(message.received_at || message.sent_at)}</div>
          </div>
        </div>
      </div>

      <div className="em-viewer-webbanner">
        <Icons.Info size={14} />
        <span>
          If there are problems with how this message is displayed,{' '}
          <button
            type="button"
            className="em-viewer-weblink"
            onClick={() => {
              const html = message.body_html
                ? resolveInlineImages(message.body_html, message.attachments)
                : `<pre style="font-family:Segoe UI,Arial,sans-serif;white-space:pre-wrap">${(message.body_text || '').replace(/</g, '&lt;')}</pre>`;
              const w = window.open('', '_blank');
              if (w) { w.document.open(); w.document.write(html); w.document.close(); }
            }}
          >
            click here
          </button>{' '}
          to view it in a web browser.
        </span>
      </div>

      {/* Attachments shown at the top, right under the sender header. */}
      {attachmentsBlock}

      <div className="em-viewer-body">
        <div className="em-viewer-paper">
          {message.body_html ? (
            <EmailBodyFrame html={resolveInlineImages(message.body_html, message.attachments)} />
          ) : (
            <pre className="em-viewer-text-body">{message.body_text || '(no content)'}</pre>
          )}
        </div>
      </div>

      {previewAtt && (() => {
        const url = previewAtt.download_url || previewAtt.file || '';
        const kind = previewKind(previewAtt);
        return (
          <div className="em-att-preview-overlay" onClick={() => setPreviewAtt(null)}>
            <div className="em-att-preview" onClick={e => e.stopPropagation()}>
              <div className="em-att-preview-head">
                <span className="em-att-preview-title" title={previewAtt.filename}>
                  <Icons.FileText size={15} /> {previewAtt.filename}
                </span>
                <div className="em-att-preview-actions">
                  <a href={url} target="_blank" rel="noopener noreferrer" className="em-att-preview-btn" title="Download">
                    <Icons.Download size={15} />
                  </a>
                  <button className="em-att-preview-btn" onClick={() => setPreviewAtt(null)} title="Close">
                    <Icons.X size={16} />
                  </button>
                </div>
              </div>
              <div className="em-att-preview-body">
                {kind === 'pdf' && (
                  <iframe src={url} title={previewAtt.filename} className="em-att-preview-frame" />
                )}
                {kind === 'image' && (
                  <img src={url} alt={previewAtt.filename} className="em-att-preview-img" />
                )}
                {kind === 'office' && (
                  <iframe
                    src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`}
                    title={previewAtt.filename}
                    className="em-att-preview-frame"
                  />
                )}
                {kind === 'none' && (
                  <div className="em-att-preview-none">
                    <Icons.FileText size={40} strokeWidth={1} />
                    <p>Preview isn’t available for this file type.</p>
                    <a href={url} target="_blank" rel="noopener noreferrer" className="em-att-preview-dl">Download to view</a>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
